<template>
  <aside class="sidebar">
    <!-- Top Section:  Mobile Menu Button-->
    <div class="sidebar-top-section">
      <div class="breadcrumbs--mobile">
        <Breadcrumbs />
      </div>

      <BaseButtonIcon
        size="medium"
        aria-label="Open menu"
        class="mobile-menu-button"
        rounded
        @click="openMobileMenu()"
      >
        <MobileMenuIcon />
      </BaseButtonIcon>
    </div>

    <!-- Desktop -->
    <nav class="desktop-navigation">
      <ul class="desktop-navigation__list">
        <li v-for="item in filteredItemsByAccess" :key="item.name">
          <NavigationItem
            :item="item"
            is-mobile-menu-open
            @exitMobileMenu="exitMobileMenu"
          />
        </li>
      </ul>
      <ul class="desktop-navigation__list">
        <li>
          <NavigationItem
            v-if="showItem(faqItem)"
            :item="faqItem"
            is-mobile-menu-open
            @exitMobileMenu="exitMobileMenu"
          />
        </li>
      </ul>
    </nav>

    <!-- Mobile -->
    <MobileNavigationModal
      v-if="isMobileMenuOpen"
      class="mobile-navigation-modal"
      :is-mobile-menu-open="isMobileMenuOpen"
      @close="exitMobileMenu"
    />
  </aside>
</template>

<script setup lang="ts">
import BaseButtonIcon from '@/components/generics/base/BaseButtonIcon.vue'
import MobileMenuIcon from '@/components/icon_components/MobileMenuIcon.vue'
import Breadcrumbs from '@/components/layout/breadcrumbs/Breadcrumbs.vue'
import MobileNavigationModal from '@/components/layout/sidebar/MobileNavigationModal.vue'
import NavigationItem from '@/components/layout/sidebar/NavigationItem.vue'
import { useGlobalNavigation } from '@/hooks/useGlobalNavigation'
import { computed, ref, unref } from 'vue'

const { faqItem, items, showItem } = useGlobalNavigation()

const isMobileMenuOpen = ref(false)

const openMobileMenu = () => {
  isMobileMenuOpen.value = true
}
const exitMobileMenu = () => {
  isMobileMenuOpen.value = false
}

const filteredItemsByAccess = computed(() => {
  const allItems = unref(items)
  return allItems.filter((item) => showItem(item))
})
</script>

<style lang="scss" scoped>
.sidebar {
  @include disable-text-selection;
  position: relative;
  background: $color-pebble;
  padding: $spacing-4xs;
  z-index: 100;

  .sidebar-top-section {
    justify-content: space-between;
    width: 100%;

    .mobile-page-title,
    .mobile-menu-button {
      display: none;
    }
  }

  .desktop-navigation {
    align-content: space-between;
    height: 100%;
    display: flex;
    flex-wrap: wrap;
    overflow-y: auto;

    &__list {
      display: flex;
      flex-direction: column;
      gap: $spacing-3xs;
      width: 100%;
      margin-top: $spacing-l;

      @media screen and (min-width: $large-desktop-width-max) {
        margin-top: 10vh;
        gap: $spacing-xs;
      }
    }
  }

  .breadcrumbs--mobile,
  .mobile-navigation-modal {
    display: none;
  }
}

/* Mobile view: */
@media screen and (max-width: $tablet-width-max) {
  .sidebar {
    padding: 0 $pageHorizontalMargin;
    background: $color-canvas;
    border-bottom: 1px solid $color-border;

    .sidebar-top-section {
      margin-bottom: 0;
      align-self: center;
      display: flex;
      align-items: center;
      justify-content: space-between;

      .mobile-page-title {
        @include subtitle;
        display: block;
        font-size: $font-size-xl;
        line-height: 1;
      }

      .mobile-menu-button {
        display: flex;
      }
    }

    .desktop-navigation {
      display: none;
    }

    .mobile-navigation-modal {
      display: table;
    }

    .breadcrumbs--mobile {
      display: block;
    }
  }
}
</style>
